<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col>
        <loading-spinner></loading-spinner>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col col="12" xl="4" md="12" xs="12">
        <!-- TODO -->
        <basic-search-box :title="$t('title.search')" @conditions="handleConditions">
        </basic-search-box>
      </v-col>

      <v-col col="12" xl="8" md="12" xs="12">
        <paginated-table :data="members" :columns="tableColumns" :title="$t('title.member_list')"
          @updatePagination="updatePagination">
          <template slot="bottom-buttons">
            <custom-button @click="excelDownload" :label="$t('button.excel_download')" color="green" size="lg" gradient
              rounded></custom-button>
          </template>

        </paginated-table>
      </v-col>
    </v-row>

    <info-modal :data="currentMember" :isVisible="showDetailInfo" @close="showDetailInfo = false"></info-modal>
  </v-container>
</template>

<script>
import BasicSearchBox from "@/components/SearchBoxs/BasicSearchBox.vue";
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import InfoModal from "@/components/cards/InfoModal.vue";
import LoadingSpinner from '@/components/common/Spinners/LoadingSpinner.vue';
import CustomButton from '@/components/common/buttons/CustomButton.vue';

import { getManagementColumns } from "@/views/MemberView/columns";
import { deleteMemberPopup } from '@/views/MemberView/popups';

import { memberApi } from "@/api/api.js";
import * as Xlsx from 'xlsx';

export default {
  components: {
    BasicSearchBox,
    PaginatedTable,
    InfoModal,
    CustomButton,
    LoadingSpinner,
  },
  data() {
    return {
      members: {
        page_info: {
          page: 1,
          size: 5,
          total_elements: 5,
          total_pages: 1,
        },
        elements: [],
      },
      isLoading: false,
      showDetailInfo: false,
      currentMember: {},
      tableColumns: getManagementColumns({
        showDetail: this.showDetail,
        delete: this.handleDelete,
      }),
      conditions: {
        startedAt: null,
        endedAt: null,
        name: null,
        email: null,
      },
    };
  },

  methods: {
    /**
     * Member 타입 명세
     * @typedef {Object} Member
     * 
     * @property {number} idx - 회원 idx
     * @property {string} member_type - 회원 타입
     * @property {string} name - 회원 국문 이름
     * @property {string} name_eng - 회원 영문 이름
     * @property {string} email - 이메일
     * @property {string} phone - 전화번호
     * @property {number} country_idx - 국가 idx
     * @property {string} organization - 조직
     * @property {string} department - 부서
     * @property {string} position - 직위
     * @property {string} referrer_name - 추천인
     * @property {string} created_at - 생성일
     */

    /**
     * 회원 정보 상세 조회 메서드
     * 
     * @description: 회원 정보 조회 메서드
     * @param {Member} item - 회원 정보
     */
    showDetail(item) {
      this.currentMember = {
        title: {
          label: this.$t("label.member_name"),
          value: item.name,
        },
        phone: {
          label: this.$t("label.member_phone"),
          value: item.phone
        },
        email: {
          label: this.$t("label.member_email"),
          value: item.email
        },
        created_at: {
          label: this.$t("label.member_created_at"),
          value: item.created_at
        },
        organization: {
          label: this.$t("label.member_organization"),
          value: item.organization
        },
        department: {
          label: this.$t("label.member_department"),
          value: item.department
        },
        position: {
          label: this.$t("label.member_position"),
          value: item.position
        },
        sns_account: {
          label: this.$t("label.member_sns_account"),
          value: item.sns_account_provider
        },
      };

      this.showDetailInfo = !this.showDetailInfo;
    },

    /**
     * 탈퇴 버튼 눌렀을 때 실행되는 메서드
     * 
     * @description: 삭제 확인 팝업 렌더링 후 삭제 버튼 누르면 탈퇴 API 실행
     * @param {Member} member - 회원 정보
     */
    async handleDelete(member) {
      const isConfirmed = await deleteMemberPopup(member);

      if (isConfirmed) {
        await this.deleteMember(member);
      }
    },

    /**
     * 회원 전체 조회 API 요청
     * 
     * @description: 회원 목록 전체 조회 메서드
     * @param {number} size - 사이즈
     * @param {number} page - 페이지
     */
    async getMembers(size, page) {
      this.isLoading = true;
      const params = {
        size: size,
        page: page,
        started_at: this.conditions.startedAt,
        ended_at: this.conditions.endedAt,
        name: this.conditions.name,
        email: this.conditions.email,
        sort_by: "created_at",
        direction: "desc",
      }

      memberApi.getMembers(params).then(res => {
        if (res && res.status === 200) {
          Object.assign(this.members, res.data.response);
        }
      }).catch().finally(() => this.isLoading = false);
    },

    /**
     * 회원 탈퇴 API 요청
     * 
     * @description: 회원이 탈퇴처리 되도록 API 요청
     * @param {Member} member - 회원 정보
     */
    async deleteMember(member) {
      memberApi.deleteMember(member).then(res => {
        if (res.status === 204) {
          this.getMembers(this.members.page_info.size, this.members.page_info.page);
        }
      })
    },

    /**
     * Pagination 타입 명세
     * @typedef {Object} Conditions
     * @property {string} page - 페이지
     * @property {string} size - 사이즈
     * @property {string} total_elements - 전체 데이터 수 
     * @property {string} total_pages - 전체 페이지 수
     */

    /** 
     * 페이지네이션 업데이트 메서드
     *  
     * @description: 페이징 정보가 변경되면 하위 컴포넌트에서 정보를 받아 업데이트 및 API 요청
     * @params {Pagination} pagination
     */
    updatePagination(pagination) {
      this.members.page_info.size = pagination.size;
      this.members.page_info.page = pagination.page;

      this.getMembers(this.members.page_info.size, this.members.page_info.page);
    },

    /**
     * Conditions 타입 명세
     * @typedef {Object} Conditions
     * @property {string} startedAt - 시작 날짜
     * @property {string} endedAt - 마지막 날짜
     * @property {string} name - 이름
     * @property {string} email - 이메일
     */

    /** 
    * 검색 조건 업데이트 메서드
    *  
    * @description: 검색 조건 업데이트 및 회원 목록 조회 API 재요청  
    * @param {Conditions} data - 검색 조건
    */
    handleConditions(data) {
      this.conditions = { ...this.conditions, ...data };
      this.members.page_info.page = 1;

      this.getMembers(this.members.page_info.size, this.members.page_info.page);
    },

    async excelDownload() {
      try {
        // 전체 데이터 가져오기
        const res = await memberApi.getMembers({ size: null, page: 1 });
        if (res && res.status === 200) {
          const data = res.data.elements;

          // 데이터 가공
          const selectedData = this.selectData(data);

          // 엑셀 생성 및 다운로드
          const workBook = Xlsx.utils.book_new();
          const workSheet = Xlsx.utils.json_to_sheet(selectedData);
          Xlsx.utils.book_append_sheet(workBook, workSheet, '회원 리스트');
          Xlsx.writeFile(workBook, '회원리스트.xlsx');
          this.notifyVue({
            message: this.$t("alert.excel_download_success"),
            style: "success",
            icon: "check_circle",
          });
        } else {
          this.notifyVue({
            message: this.$t("alert.excel_download_failed"),
            style: "danger",
          });
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },

    selectData(data) {
      return data.map(item => ({
        "이름": item.name,
        "전화번호": item.phone,
        "이메일": item.email,
        "가입일": item.created_at,
        "소속": item.organization,
        "부서": item.department,
        "직위": item.position,
      }))
    },
    /* ****************************************************************************************
    * 알럿
    * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
    } = {}) {
      this.$notify({
        timeout: 8000,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    }
  },

  mounted() {
    this.getMembers(this.members.page_info.size, this.members.page_info.page);
  },
};
</script>

<style scoped></style>
